/* eslint-disable @typescript-eslint/no-explicit-any */
import {Area, Line} from "@ant-design/charts";
import {
  getSensorStatistics,
  getSensorStatisticsData,
  getSensorStatisticsSearchConditions,
} from "@api/sensorStatistics";
import CustomButton from "@component/CustomButton";
import CustomDatePicker from "@component/CustomDatePicker";
import CustomSelect from "@component/CustomSelect";
import CustomTable from "@component/CustomTable";
import CustomValidFormInput from "@component/form/CustomValidFormInput";
import IconBtnSearch from "@icon/IconBtnSearch";
import IconTitle from "@icon/IconTitle";
import {DashboardDetailParam, SensorDetailSearchConditions} from "@interface/dashboard/Dashboard";
import {
  CountryItem,
  DateInfo,
  SensorStatisticsData,
  SensorStatisticsSearchParam,
} from "@interface/sensor/SensorStatistics";
import { month } from "@interface/sensor/initData";
import { Card, Col, message, Row, TableColumnsType } from "antd";
import { Option } from "antd/lib/mentions";
import { HttpStatusCode } from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { AiOutlineSound } from "react-icons/ai";
import { FaThermometerHalf } from "react-icons/fa";
import { GiGooeyImpact } from "react-icons/gi";
import { HiMiniBellAlert } from "react-icons/hi2";
import { MdGpsFixed } from "react-icons/md";
import { PiBatteryPlusVerticalFill } from "react-icons/pi";
import { RxAngle } from "react-icons/rx";
import { WiHumidity } from "react-icons/wi";
import { useLocation } from "react-router-dom";
import { SENSOR_STATISTICS_HEADER } from "@util/constants";
import ExcelJS from "exceljs";

const SensorStatistics = () => {
  const location = useLocation();
  const {
    control: searchFormControl,
    handleSubmit: searchFormHandleSubmit,
    setValue,
    getValues,
    watch,
  } = useForm<SensorStatisticsSearchParam>({ mode: "onSubmit" });
  // setValue만으로는 리렌더링이 되지 않으므로 watch로 구독해 DatePicker 표시값을 동기화
  const searchYear = watch("searchYear");
  const [monthList, setMonthList] = useState<DateInfo[]>(month);
  const [ntnlList, setNtnlList] = useState<CountryItem[]>([]);
  const [
    sensorDetailSearchConditionsState,
    setSensorDetailSearchConditionsState,
  ] = useState<SensorDetailSearchConditions>({
    sggList: [],
  });
  const [sggCd, setSggCd] = useState<string>("");
  const record = location?.state?.record;
  const [dataType, setDataType] = useState<string>("month");
  const [data, setData] = useState<SensorStatisticsData>();

  const downloadExcel = async (type: string) => {
    console.log(data?.sggCd);
    console.log(
        sensorDetailSearchConditionsState.sggList.filter(
            (item) => item.value === data?.sggCd
        )[0]
    );
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Sheet 1");
    const excelData = [
      { data: ["[검색조건]"] },
      { data: ["자료구분", data?.dataType === "year" ? "년" : "월"] },
      { data: ["기간", data?.searchYear] },
      {
        data: [
          "권역",
          data?.sggCd
              ? sensorDetailSearchConditionsState.sggList.filter(
                  (item) => item.value === data?.sggCd
              )[0].label
              : "전체",
        ],
      },
      { data: ["국가지점/다목적위치표지판 번호", data?.ntnlPntNo] },
      { data: [""] },
      { data: SENSOR_STATISTICS_HEADER },
    ];

    excelData.forEach((item, index) => {
      sheet.getRow(index + 1).values = [...item.data];
    });
    sheet.getRow(7).eachCell((cell: ExcelJS.Cell) => {
      cell.style.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
    });
    data?.graphList.forEach((item: any, index) => {
      sheet.getRow(index + 8).values = [
        item.dateTime,
        item.battery1,
        item.battery1Alarm,
        item.battery1ChangeCount,
        item.battery2,
        item.battery2Alarm,
        item.battery2ChangeCount,
        item.battery3,
        item.battery3Alarm,
        item.battery3ChangeCount,
        item.temp,
        item.tempAlarm,
        item.hum,
        item.humAlarm,
        item.gpsLat,
        item.gpsLon,
        item.gpsLatRef,
        item.gpsLonRef,
        item.gpsAlarm,
        item.xslope + "-" + item.yslope + "-" + item.zslope,
        item.slopeAlarm,
        item.sound,
        item.soundAlarm,
        item.shock,
        item.shockAlarm,
      ];
      sheet.getRow(index + 8).eachCell((cell: ExcelJS.Cell) => {
        cell.style.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
      });
    });

    sheet.columns.forEach((column, index) => {
      const header = sheet
          .getRow(7)
          .getCell(index + 1)
          .value?.toString();
      if (index === 0) {
        column.width = 30;
      } else {
        column.width = header ? header.length + 10 : 12;
      }
    });

    let buffer;
    if (type === "01") {
      buffer = await workbook.csv.writeBuffer();
    } else {
      buffer = await workbook.xlsx.writeBuffer();
    }

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = type === "01" ? "센서통계.csv" : "센서통계.xlsx";
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  const configSetting = (
      data: any,
      color: "green" | "red",
      dateFormat: string
  ) => {

    const config = {
      data: {
        value: data,
      },
      xField: (d: any) =>
          dayjs(d.date).format(dateFormat === "year" ? "MM" : "DD"),
      yField: "cnt",
      colorField: "type",
      axis: {
        // y: { labelFormatter: '~s' },
        x: {
          title: dateFormat === "year" ? "월(month)" : "일(day)", // x축 제목 (조회 구분에 따라 월/일)
          titleFontSize: 11,
          labelAutoRotate: false, // 라벨 자동 회전(기울임) 비활성화
          labelTransform: "rotate(0)", // 라벨을 똑바로(세로 방향 정렬) 표시
          labelAutoHide: true, // 겹칠 경우 회전 대신 일부 라벨 숨김
        },
      },
      sortBy: (d: any) => d.date,
      seriesField: 'type',
      style: {
        lineWidth:3
      },
      paddingRight: 12, // 차트 우측 내부 여백 축소
    };
    return config;
  };
  const sensorColumns: TableColumnsType<CountryItem> = [
    {
      title: "국가지점번호",
      dataIndex: "ntnlPntNo",
      key: "ntnlPntNo",
      align: "center",
    },
    {
      title: "다목적위치표지판번호",
      dataIndex: "lctnSignNo",
      key: "lctnSignNo",
      align: "center",
    },
  ];

  const monthClick = (record: DateInfo) => {
    const newList = month.map((item) => {
      return { ...item, active: item.month === record.month };
    });
    setMonthList(newList);
  };

  const dataSearch = (param: SensorStatisticsSearchParam) => {
    ntnlListSearch(param);
    let searchYear = getValues("searchYear");

    if (dataType === "month") {
      const activeMonthList = monthList.filter((month) => month.active);
      if (activeMonthList.length === 0) {
        message.error("조회하실 기준월을 선택해주세요");
        return;
      }
      let activeMonth = monthList
          .filter((month) => month.active)[0]
          .month.toString();

      if (activeMonth.length === 1) {
        activeMonth = "0" + activeMonth;
      }

      searchYear += "-" + activeMonth;
    }

    getSensorStatisticsData({
      ...param,
      dataType: dataType,
      searchYear: searchYear,
      sggCd: sggCd,
    }).then((res) => {
      if (res.code === HttpStatusCode.Ok && res.item) {
        const batteryGraph = res.item.graphList.map((item) => {
          return {
            date: item.dateTime,
            type: "배터리1",
            cnt: item.battery1,
          };
        });
        res.item.graphList.map((item) => {
          batteryGraph.push({
            date: item.dateTime,
            type: "배터리2",
            cnt: item.battery2,
          });
        });
        res.item.graphList.map((item) => {
          batteryGraph.push({
            date: item.dateTime,
            type: "배터리3",
            cnt: item.battery3 ? item.battery3 : 0,
          });
        });

        // const tempGraph :any[]= [];
        // res.item.graphList.map((item) => {
        //   return {
        //     date: item.dateTime,
        //     type: "온도(센서)",
        //     cnt: item.temp,
        //   };
        // });
        // const humGraph :any[]= [];
        // res.item.graphList.map((item) => {
        //   return {
        //     date: item.dateTime,
        //     type: "습도(센서)",
        //     cnt: item.hum,
        //   };
        // });
        const tempGraph: any[] = res.item.graphList.map((item) => {
          return {
            date: item.dateTime,
            type: "온도(센서)",
            cnt: item.temp,
          };
        });

        const humGraph: any[] = res.item.graphList.map((item) => {
          return {
            date: item.dateTime,
            type: "습도(센서)",
            cnt: item.hum,
          };
        });

        res.item.weatherApiList.map((item) => {
          tempGraph.push({
            date: item.dateTime,
            type: "온도(기상청)",
            cnt: item.temp,
          });
          humGraph.push({
            date: item.dateTime,
            type: "습도(기상청)",
            cnt: item.hum,
          });
        });
        res.item.gpsLineChart = res.item.graphList.map((item) => {
          return {
            date: item.dateTime,
            type: "GNSS",
            cnt: item.gpsLat,
          };
        });

        res.item.soundLineChart = res.item.graphList.map((item) => {
          return {
            date: item.dateTime,
            type: "데시벨",
            cnt: item.sound,
          };
        });

        res.item.impactLineChart = res.item.graphList.map((item) => {
          return {
            date: item.dateTime,
            type: "충격",
            cnt: item.shock,
          };
        });

        res.item.alarmLineChart = res.item.graphList.map((item) => {
          return {
            date: item.dateTime,
            type: "알람",
            cnt:
                item.battery1Alarm +
                item.battery2Alarm +
                item.battery3Alarm +
                item.gpsAlarm +
                item.humAlarm +
                item.tempAlarm +
                item.slopeAlarm +
                item.soundAlarm +
                item.shockAlarm,
          };
        });

        const gpsGraph = res.item.graphList.map((item) => {
          return {
            date: item.dateTime,
            type: "경도",
            cnt: item.gpsLon,
          };
        });
        res.item.graphList.map((item) => {
          gpsGraph.push({
            date: item.dateTime,
            type: "위도",
            cnt: item.gpsLat,
          });
        });
        res.item.graphList.map((item) => {
          gpsGraph.push({
            date: item.dateTime,
            type: "경도(기준값)",
            cnt: item.gpsLonRef ? item.gpsLonRef : 0,
          });
        });
        res.item.graphList.map((item) => {
          gpsGraph.push({
            date: item.dateTime,
            type: "위도(기준값)",
            cnt: item.gpsLatRef ? item.gpsLatRef : 0,
          });
        });

        const slopeGraph: any[] = res.item.graphList.map((item) => {
          return {
            date: item.dateTime,
            type: "x기울기",
            cnt: item.xslope,
          };
        });
        slopeGraph.push({
          date: "2024-11",
          type: "y기울기",
          cnt: undefined,
        });
        res.item.graphList.map((item) => {
          slopeGraph.push({
            date: item.dateTime,
            type: "y기울기",
            cnt: item.xslope,
          });
        });

        res.item.graphList.map((item) => {
          slopeGraph.push({
            date: item.dateTime,
            type: "z기울기",
            cnt: item.zslope,
          });
        });

        tempGraph.sort((a, b) => {
          return a.date > b.date ? 1 : -1;
        });
        humGraph.sort((a, b) => {
          return a.date > b.date ? 1 : -1;
        });
        res.item.batteryLineChart = batteryGraph;
        res.item.gpsLineChart = gpsGraph;
        res.item.angleLineChart = slopeGraph;
        res.item.temperatureLineChart = tempGraph;
        res.item.humidityLineChart = humGraph;
        setData(res.item);
      }
    });
  };

  const initParam = () => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = String(now.getFullYear());
    if (record) {
      setValue("ntnlPntNo", record.ntnlPntNo);
      setSggCd(record.sggCd);
    } else {
      setSggCd("");
    }

    setValue("dataType", "month");
    setValue("searchYear", currentYear);
    setMonthList(
        month.map((item) => {
          if (item.month === currentMonth) {
            item.active = true;
          }
          return item;
        })
    );

    dataSearch({
      dataType: dataType,
      searchYear: currentYear,
      sggCd: record ? record.sggCd : "",
      ntnlPntNo: record ? record.ntnlPntNo : "",
      month: currentMonth,
    });
  };

  const ntnlListSearch = (data: SensorStatisticsSearchParam) => {
    getSensorStatistics(data).then((res) => {
      if (res.code === HttpStatusCode.Ok && res.item) {
        setNtnlList(res.item);
      }
    });
  };

  const sensorStatisticsSearchConditions = () => {
    getSensorStatisticsSearchConditions().then((res) => {
      if (res.code === HttpStatusCode.Ok && res.item) {
        setSensorDetailSearchConditionsState(res.item);
        initParam();
        ntnlListSearch({
          sggCd: "",
          ntnlPntNo: "",
          dataType : "",
          searchYear : "",
          month : 12
        });
      }
    });
  };

  useEffect(() => {
    sensorStatisticsSearchConditions();
  }, []);

  return (
      <>
        <section className="title-wrap">
          <div className="box-flex">
            <h2 className="title">센서 통계</h2>
          </div>
          <section className="button-wrap">
            <div className="box-btn">
              <CustomButton
                  type="primary"
                  onClick={searchFormHandleSubmit(dataSearch)}
              >
                <IconBtnSearch />
                조회
              </CustomButton>
            </div>
          </section>
        </section>
        <section className="search-wrap">
          <form>
            <div className="deps">
              <span>자료구분</span>
              <CustomSelect
                  value={dataType}
                  onChange={(value: string) => {
                    if (value) {
                      setDataType(value);
                      if (value === "year") {
                        setMonthList(
                            month.map((item) => {
                              return { ...item, active: false };
                            })
                        );
                      }
                    }
                  }}
                  options={[
                    { value: "month", label: "월" },
                    { value: "year", label: "년" },
                  ]}
              />
              <span>기준년도</span>
              <CustomDatePicker
                  style={{ width: "200px" }}
                  picker="year"
                  value={searchYear ? dayjs(searchYear, "YYYY") : null}
                  onChange={(value) => {
                    if (value) {
                      setValue("searchYear", dayjs(value).format("YYYY"));
                    } else {
                      setValue("searchYear", null);
                    }
                  }}
              />

              <span>권역</span>
              <CustomSelect value={sggCd} onChange={(value) => setSggCd(value)}>
                <Option value={""}>전체</Option>
                {sensorDetailSearchConditionsState.sggList.map((item, index) => {
                  return (
                      <Option key={`sgg_${index}`} value={item.value}>
                        {item.label}
                      </Option>
                  );
                })}
              </CustomSelect>
              <span>국가지점/다목적위치표지판 번호</span>
              <CustomValidFormInput
                  name={"ntnlPntNo"}
                  control={searchFormControl}
              />
            </div>
            <div className="deps">
              <span>기준월</span>
              <div className="box-month-btn">
                {monthList.map((item) => {
                  return (
                      <CustomButton
                          disabled={dataType === "year"}
                          key={item.month}
                          className={`btn ${item.active && "on"}`}
                          onClick={() => {
                            getValues("dataType") !== "year" && monthClick(item);
                          }}
                      >
                        {item.month}월
                      </CustomButton>
                  );
                })}
              </div>
            </div>
          </form>
        </section>
        <section className="board-wrap half-wrap type02">
          <div>
            <div className="board-title-wrap">
              <h3 className="title">
                <IconTitle />
                국가지점번호 목록
              </h3>
            </div>
            <div className="board-cont-wrap fixed-height">
              <CustomTable
                  rowNoFlag={true}
                  columns={sensorColumns}
                  dataSource={ntnlList.filter((ntnl) => {
                    if (sggCd === "") {
                      return true;
                    } else {
                      return ntnl.sggCd == sggCd;
                    }
                  })}
              />
            </div>
          </div>

          <div>
            {data && (
                <>
                  <div className="board-title-wrap">
                    <h3 className="title">
                      <IconTitle />
                      그래프
                    </h3>
                    <div className="box-btn">
                      <CustomButton
                          type="default"
                          size="small"
                          onClick={() => downloadExcel("01")}
                      >
                        CSV
                      </CustomButton>
                      <CustomButton
                          type="default"
                          size="small"
                          onClick={() => downloadExcel("02")}
                      >
                        EXCEL
                      </CustomButton>
                    </div>
                  </div>

                  <div className="board-cont-wrap">
                    <Row gutter={16}>
                      <Col span={8}>
                        <Card
                            className="stat-chart-card"
                            bordered={false}
                            actions={[<>배터리</>]}
                            bodyStyle={{ paddingBottom: "0px" }}
                        >
                          <p>
                            <PiBatteryPlusVerticalFill /> 배터리(%)
                          </p>
                          <p
                              style={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            누적값 :{" "}
                            {data.graphList.reduce((acc, cur) => {
                              return (
                                  acc +
                                  cur.battery1ChangeCount +
                                  cur.battery2ChangeCount +
                                  cur.battery3ChangeCount
                              );
                            }, 0)}
                          </p>
                          <div style={{ height: "150px" }}>
                            <Line
                                {...configSetting(
                                    data.batteryLineChart,
                                    "green",
                                    data.dataType
                                )}
                            />
                          </div>
                        </Card>
                      </Col>
                      <Col span={8}>
                        <Card
                            className="stat-chart-card"
                            bordered={false}
                            actions={[<>온도</>]}
                            bodyStyle={{ paddingBottom: "0px" }}
                        >
                          <p>
                            <FaThermometerHalf /> 온도(℃)
                          </p>
                          <div style={{ height: "170px" }}>
                            <Line
                                {...configSetting(
                                    data.temperatureLineChart,
                                    "green",
                                    data.dataType
                                )}
                            />
                          </div>
                        </Card>
                      </Col>
                      <Col span={8}>
                        <Card
                            className="stat-chart-card"
                            bordered={false}
                            actions={[<>습도</>]}
                            bodyStyle={{ paddingBottom: "0px" }}
                        >
                          <p>
                            <WiHumidity /> 습도(%)
                          </p>
                          <div style={{ height: "170px" }}>
                            <Line
                                {...configSetting(
                                    data.humidityLineChart,
                                    "green",
                                    data.dataType
                                )}
                            />
                          </div>
                        </Card>
                      </Col>
                    </Row>
                    <Row gutter={16} className={"mt10"}>
                      <Col span={8}>
                        <Card
                            className="stat-chart-card"
                            bordered={false}
                            actions={[<>GNSS</>]}
                            bodyStyle={{ paddingBottom: "0px" }}
                        >
                          <p>
                            <MdGpsFixed /> GNSS(좌표)
                          </p>
                          <p
                              style={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            누적값 :{" "}
                            {data.graphList.reduce((acc, cur) => {
                              return acc + cur.gpsAlarm;
                            }, 0)}
                          </p>
                          <div style={{ height: "150px" }}>
                            <Line
                                {...configSetting(
                                    data.gpsLineChart,
                                    "green",
                                    data.dataType
                                )}
                            />
                          </div>
                        </Card>
                      </Col>
                      <Col span={8}>
                        <Card
                            className="stat-chart-card"
                            bordered={false}
                            actions={[<>기울기</>]}
                            bodyStyle={{ paddingBottom: "0px" }}
                        >
                          <p>
                            <RxAngle /> 기울기(°)
                          </p>
                          <p
                              style={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            누적값 :{" "}
                            {data.graphList.reduce((acc, cur) => {
                              return acc + cur.slopeAlarm;
                            }, 0)}
                          </p>
                          <div style={{ height: "150px" }}>
                            <Line
                                {...configSetting(
                                    data.angleLineChart,
                                    "green",
                                    data.dataType
                                )}
                            />
                          </div>
                        </Card>
                      </Col>
                      <Col span={8}>
                        <Card
                            className="stat-chart-card"
                            bordered={false}
                            actions={[<>데시벨</>]}
                            bodyStyle={{ paddingBottom: "0px" }}
                        >
                          <p>
                            <AiOutlineSound /> 데시벨(dB)
                          </p>
                          <p
                              style={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            누적값 :{" "}
                            {data.graphList.reduce((acc, cur) => {
                              return acc + cur.soundAlarm;
                            }, 0)}
                          </p>
                          <div style={{ height: "150px" }}>
                            <Line
                                {...configSetting(
                                    data.soundLineChart,
                                    "green",
                                    data.dataType
                                )}
                            />
                          </div>
                        </Card>
                      </Col>
                    </Row>
                    <Row gutter={16} className={"mt10"}>
                      <Col span={8}>
                        <Card
                            className="stat-chart-card"
                            bordered={false}
                            actions={[<>충격</>]}
                            bodyStyle={{ paddingBottom: "0px" }}
                        >
                          <p>
                            <GiGooeyImpact /> 충격(횟수)
                          </p>
                          <p
                              style={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            누적값 :{" "}
                            {data.graphList.reduce((acc, cur) => {
                              return acc + cur.shockAlarm;
                            }, 0)}
                          </p>
                          <div style={{ height: "150px" }}>
                            <Line
                                {...configSetting(
                                    data.impactLineChart,
                                    "red",
                                    data.dataType
                                )}
                            />
                          </div>
                        </Card>
                      </Col>
                      {/* <Col span={8}>
                <Card
                  className="stat-chart-card"
                  bordered={false}
                  actions={[<>스위치</>]}
                  bodyStyle={{ paddingBottom: '0px' }}
                >
                  <p>
                    <RiAlarmWarningFill /> 비상스위치
                  </p>
                  <div style={{ height: '150px' }}>
                    <Area {...switchConfig} />
                  </div>
                </Card>
              </Col> */}
                      <Col span={8}>
                        <Card
                            className="stat-chart-card"
                            bordered={false}
                            actions={[<>알람</>]}
                            bodyStyle={{ paddingBottom: "0px" }}
                        >
                          <p>
                            <HiMiniBellAlert /> 알람(건수)
                          </p>
                          <p
                              style={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            누적값 :{" "}
                            {data.alarmLineChart.reduce((acc, cur) => {
                              return acc + (cur.cnt ? cur.cnt : 0);
                            }, 0)}
                          </p>
                          <div style={{ height: "150px" }}>
                            <Line
                                {...configSetting(
                                    data.alarmLineChart,
                                    "red",
                                    data.dataType
                                )}
                            />
                          </div>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                </>
            )}
          </div>
        </section>
      </>
  );
};
export default SensorStatistics;
